import { formatMoney, TenantCurrency } from 'src/common/currency';
import { ProviderReferenceHolder } from 'src/financial/financial-data/providerReferenceHolders';

type FeedProduct = {
  provider_reference_number: string;
  policy_number: string | null;
  value: number | null;
  holders: ProviderReferenceHolder[];
};

type FeedIdentifier = 'PROVIDER_REFERENCE_NUMBER' | 'POLICY_NUMBER';

type SwappedIdentifier = {
  asset_field: FeedIdentifier;
  number: string;
  both_fields: boolean;
  feed_provider_reference_number: string;
  feed_policy_number: string | null;
  provider: string;
  product_name: string | null;
};

type FeedMatchProblemKind =
  | 'PROVIDER_REFERENCE_NUMBER_CONFLICT'
  | 'POLICY_NUMBER_CONFLICT'
  | 'COMPETING_ASSET'
  | 'SEVERAL_WRAPPERS_MISSING_POLICY_NUMBER'
  | 'SEVERAL_WRAPPERS_MISSING_PROVIDER_REFERENCE_NUMBER'
  | 'SEVERAL_WRAPPERS_WITH_THE_SAME_IDENTIFIERS'
  | 'FEED_SENDS_A_POLICY_NUMBER_IT_LACKS'
  | 'FEED_SENDS_A_PROVIDER_REFERENCE_NUMBER_IT_LACKS'
  | 'SWAPPED_IDENTIFIER';

export type FeedMatch = {
  outcome: 'MATCH' | 'CONFLICT' | 'AMBIGUOUS' | 'NO_MATCH';
  problem: FeedMatchProblemKind | null;
  is_connected: boolean;
  asset_provider_reference_number: string | null;
  asset_policy_number: string | null;
  feed_products: FeedProduct[];
  competing_holders: ProviderReferenceHolder[];
  competitors_could_be_joint: boolean;
  asset_can_be_held_jointly: boolean;
  swapped_identifier: SwappedIdentifier | null;
};

export type HolderSentence = {
  lead: string;
  holders: ProviderReferenceHolder[];
};

export type FeedMatchExplanation = {
  statement: string;
  holderSentences: HolderSentence[];
  action: string;
};

type DescribeOptions = {
  currency: TenantCurrency;
  ownersEnabled: boolean;
  // The asset form's own name for the provider reference number field, in lower case: a defined
  // benefit pension calls it "pension scheme reference number".
  referenceLabel: string;
};

const listFormat = new Intl.ListFormat('en-GB', { style: 'long', type: 'conjunction' });

const distinct = (values: (string | null)[]) =>
  [...new Set(values)].filter((value): value is string => value !== null);

const plural = (word: string, count: number) => (count === 1 ? word : `${word}s`);

const capitalise = (text: string) => `${text.charAt(0).toUpperCase()}${text.slice(1)}`;

const goal = (asset = 'this asset') => `to connect ${asset} to the feed`;

const withValue = (description: string, value: number | null, currency: TenantCurrency) =>
  value === null ? description : `${description} (${formatMoney(value, currency)})`;

const conflictHolderSentences = (
  feedMatch: FeedMatch,
  conflicting: FeedIdentifier,
): HolderSentence[] =>
  feedMatch.feed_products.flatMap(product => {
    const named =
      conflicting === 'PROVIDER_REFERENCE_NUMBER'
        ? product.provider_reference_number
        : product.policy_number;
    return product.holders.length > 0 && named !== null
      ? [{ lead: `${named} is on`, holders: product.holders }]
      : [];
  });

const thatWrapper = (feedMatch: FeedMatch) =>
  feedMatch.feed_products.length === 1 ? 'that wrapper' : 'one of those wrappers';

// A joint owner's own wrapper under the same policy number is the usual reason for a second
// provider reference number, but only for a product that can be held jointly.
const anotherWrapper = (feedMatch: FeedMatch, conflicting: FeedIdentifier) =>
  conflicting === 'PROVIDER_REFERENCE_NUMBER' && feedMatch.asset_can_be_held_jointly
    ? "a different wrapper, such as a joint owner's own"
    : 'a different wrapper';

// Both numbers can be right: the feed's product may be another wrapper whose own row hasn't
// arrived yet, so the adviser is told what to do either way rather than to correct one. A connected
// asset's identifiers are frozen, so it is disconnected to renumber it.
const describeConflict = (
  feedMatch: FeedMatch,
  conflicting: FeedIdentifier,
  reference: string,
): FeedMatchExplanation => {
  const { feed_products: products, is_connected: isConnected } = feedMatch;
  const isPrnConflict = conflicting === 'PROVIDER_REFERENCE_NUMBER';
  const field = isPrnConflict ? reference : 'policy number';
  const holderSentences = conflictHolderSentences(feedMatch, conflicting);
  const other = anotherWrapper(feedMatch, conflicting);
  if (isPrnConflict) {
    const references = distinct(products.map(product => product.provider_reference_number));
    const under = `${plural(reference, references.length)} ${listFormat.format(references)}`;
    return isConnected
      ? {
          statement: `The data feed also has policy number ${feedMatch.asset_policy_number} under ${under}, which no asset is connected to.`,
          holderSentences,
          action: `If the provider has renumbered this wrapper, disconnect this asset, change its ${field} to match and connect it again. If it's ${other}, add it as its own asset.`,
        }
      : {
          statement: `The data feed has policy number ${feedMatch.asset_policy_number} under ${under}, not ${feedMatch.asset_provider_reference_number}.`,
          holderSentences,
          action: `If this asset is ${thatWrapper(feedMatch)}, correct its ${field} ${goal('it')}. If it's ${other}, it can connect once the data feed sends that wrapper.`,
        };
  }
  const policyNumbers = distinct(products.map(product => product.policy_number));
  const withPolicyNumbers = `${plural('policy number', policyNumbers.length)} ${listFormat.format(policyNumbers)}`;
  return isConnected
    ? {
        statement: `The data feed also has ${reference} ${feedMatch.asset_provider_reference_number} with ${withPolicyNumbers}, which no asset is connected to.`,
        holderSentences,
        action: `If the provider has renumbered this wrapper, disconnect this asset, change its ${field} to match and connect it again. If it's ${other}, add it as its own asset.`,
      }
    : {
        statement: `The data feed has ${reference} ${feedMatch.asset_provider_reference_number} with ${withPolicyNumbers}, not ${feedMatch.asset_policy_number}.`,
        holderSentences,
        action: `If this asset is ${thatWrapper(feedMatch)}, correct its ${field} ${goal('it')}. If it's ${other}, it can connect once the data feed sends that wrapper.`,
      };
};

const describeWrapper = (product: FeedProduct, reference: string) =>
  product.policy_number === null
    ? `${reference} ${product.provider_reference_number}`
    : `${reference} ${product.provider_reference_number} and policy number ${product.policy_number}`;

const competitionStatement = (isConnected: boolean, otherCount: number, wrapper: string) => {
  if (isConnected) {
    return otherCount === 1
      ? `Another connected asset has the same wrapper in the data feed as this one, so the feed updates neither: ${wrapper}.`
      : `Other connected assets have the same wrapper in the data feed as this one, so the feed updates none of them: ${wrapper}.`;
  }
  return otherCount === 1
    ? `Another asset matches the same wrapper in the data feed as this one: ${wrapper}.`
    : `Other assets match the same wrapper in the data feed as this one: ${wrapper}.`;
};

const describeCompetition = (
  feedMatch: FeedMatch,
  { currency, ownersEnabled, referenceLabel }: DescribeOptions,
): FeedMatchExplanation => {
  const { is_connected: isConnected, competing_holders: others } = feedMatch;
  const [product] = feedMatch.feed_products;
  const wrapper = product
    ? withValue(describeWrapper(product, referenceLabel), product.value, currency)
    : 'the wrapper';
  const correction = isConnected
    ? 'disconnect whichever is wrong'
    : `correct the identifiers on whichever is wrong ${goal()}`;
  // With owners on, "Joint asset" sits in the summary a connected asset shows read-only.
  const jointControl = ownersEnabled
    ? 'turn on "Joint asset" on each client\'s copy, disconnecting any copy connected to the data feed first'
    : 'answer yes to "Is this joint?" on each client\'s copy';
  const askTheirAdviser = others.some(holder => !holder.client_full_name)
    ? ", asking the adviser of any copy you can't open"
    : '';
  return {
    statement: competitionStatement(isConnected, others.length, wrapper),
    holderSentences: [
      { lead: others.length === 1 ? 'The other is' : 'The others are', holders: others },
    ],
    action: feedMatch.competitors_could_be_joint
      ? `If they are copies of one joint holding, ${jointControl}${askTheirAdviser}. Otherwise ${correction}.`
      : `${capitalise(correction)}${askTheirAdviser}.`,
  };
};

const policyNumberWrappers = (products: FeedProduct[], currency: TenantCurrency) =>
  products.map(product =>
    withValue(
      product.policy_number ? `policy number ${product.policy_number}` : 'no policy number',
      product.value,
      currency,
    ),
  );

const referenceWrappers = (products: FeedProduct[], reference: string, currency: TenantCurrency) =>
  products.map(product =>
    withValue(`${reference} ${product.provider_reference_number}`, product.value, currency),
  );

const theseWrappers = (count: number) => (count === 1 ? 'this wrapper' : 'these wrappers');

const ifItIsThisAsset = (count: number) =>
  count === 1 ? 'If that is this asset' : 'If one of them is this asset';

const describeSwap = (swapped: SwappedIdentifier, reference: string): FeedMatchExplanation => {
  const fieldNames: Record<FeedIdentifier, string> = {
    PROVIDER_REFERENCE_NUMBER: reference,
    POLICY_NUMBER: 'policy number',
  };
  const assetField = fieldNames[swapped.asset_field];
  const feedField =
    fieldNames[
      swapped.asset_field === 'PROVIDER_REFERENCE_NUMBER'
        ? 'POLICY_NUMBER'
        : 'PROVIDER_REFERENCE_NUMBER'
    ];
  const source = [swapped.provider, swapped.product_name].filter(Boolean).join(', ');
  if (swapped.both_fields) {
    return {
      statement: `This asset's ${reference} and policy number are the other way round from the data feed's wrapper with ${reference} ${swapped.feed_provider_reference_number} and policy number ${swapped.feed_policy_number} (${source}).`,
      holderSentences: [],
      action: 'If this asset is that wrapper, swap the two numbers over.',
    };
  }
  const otherIdentifier =
    swapped.asset_field === 'PROVIDER_REFERENCE_NUMBER'
      ? `${reference} ${swapped.feed_provider_reference_number}`
      : swapped.feed_policy_number && `policy number ${swapped.feed_policy_number}`;
  const wrapper = otherIdentifier ? `the wrapper with ${otherIdentifier}` : 'a wrapper';
  return {
    statement: `${swapped.number} is in this asset's ${assetField} field, but the data feed has it as the ${feedField} of ${wrapper} (${source}).`,
    holderSentences: [],
    action: `If this asset is that wrapper, move ${swapped.number} to the ${feedField} field.`,
  };
};

/** The adviser-facing explanation of the problem the backend found with the asset's feed match. */
export const describeFeedMatch = (
  feedMatch: FeedMatch,
  options: DescribeOptions,
): FeedMatchExplanation | null => {
  const { currency, referenceLabel } = options;
  const {
    feed_products: products,
    asset_provider_reference_number: assetReference,
    asset_policy_number: assetPolicyNumber,
  } = feedMatch;
  switch (feedMatch.problem) {
    case 'PROVIDER_REFERENCE_NUMBER_CONFLICT':
      return describeConflict(feedMatch, 'PROVIDER_REFERENCE_NUMBER', referenceLabel);
    case 'POLICY_NUMBER_CONFLICT':
      return describeConflict(feedMatch, 'POLICY_NUMBER', referenceLabel);
    case 'COMPETING_ASSET':
      return describeCompetition(feedMatch, options);
    case 'SEVERAL_WRAPPERS_MISSING_POLICY_NUMBER':
      return {
        statement: `The data feed has these wrappers with ${referenceLabel} ${assetReference}: ${listFormat.format(policyNumberWrappers(products, currency))}.`,
        holderSentences: [],
        action: `Add this asset's policy number ${goal('it')}.`,
      };
    case 'SEVERAL_WRAPPERS_MISSING_PROVIDER_REFERENCE_NUMBER':
      return {
        statement: `The data feed has these wrappers with policy number ${assetPolicyNumber}: ${listFormat.format(referenceWrappers(products, referenceLabel, currency))}.`,
        holderSentences: [],
        action: `Add this asset's ${referenceLabel} ${goal('it')}.`,
      };
    case 'SEVERAL_WRAPPERS_WITH_THE_SAME_IDENTIFIERS':
      return {
        statement: `The data feed has more than one wrapper with ${referenceLabel} ${assetReference} and policy number ${assetPolicyNumber}, so it can't tell which one is this asset.`,
        holderSentences: [],
        action: 'Check these numbers with the provider.',
      };
    // A connected asset only takes rows whose identifiers it has all of, so these rows, which
    // carry one it lacks, reach it only once the adviser confirms which is this wrapper.
    case 'FEED_SENDS_A_POLICY_NUMBER_IT_LACKS':
      return {
        statement: `The data feed now sends ${theseWrappers(products.length)} under this asset's ${referenceLabel} ${assetReference}: ${listFormat.format(policyNumberWrappers(products, currency))}.`,
        holderSentences: [],
        action: `${ifItIsThisAsset(products.length)}, disconnect it, add its policy number and connect it again.`,
      };
    case 'FEED_SENDS_A_PROVIDER_REFERENCE_NUMBER_IT_LACKS':
      return {
        statement: `The data feed now sends ${theseWrappers(products.length)} under this asset's policy number ${assetPolicyNumber}: ${listFormat.format(referenceWrappers(products, referenceLabel, currency))}.`,
        holderSentences: [],
        action: `${ifItIsThisAsset(products.length)}, disconnect it, add its ${referenceLabel} and connect it again.`,
      };
    case 'SWAPPED_IDENTIFIER':
      return feedMatch.swapped_identifier
        ? describeSwap(feedMatch.swapped_identifier, referenceLabel)
        : null;
    case null:
      return null;
  }
};
